import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Inline picker for linking/unlinking an opponent profile to a draft slot or league team.
 *
 * @param {Object|null}  currentOpponent  - Currently linked opponent { id, displayName } or null
 * @param {Function}     onLink           - Called with opponentId when an existing opponent is selected
 * @param {Function}     onUnlink         - Called when "Remove link" is selected
 * @param {Function}     onCreateAndLink  - Called with displayName string to create-then-link
 * @param {Array}        opponents        - Full opponent list [{ id, displayName, nickname }]
 * @param {boolean}      loading          - Disables the trigger while an async operation is in flight
 */
function OpponentPicker({ currentOpponent, onLink, onUnlink, onCreateAndLink, opponents = [], loading = false, suggestedName }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dialogName, setDialogName] = useState('');
  // The draft board (and, less severely, other tables this is used in) renders a team's picker
  // trigger inside a header cell with its own `overflow: hidden` (for team-name ellipsis
  // truncation) nested inside a scrolling container -- an absolutely-positioned dropdown there
  // gets clipped down to nearly nothing by the cell's own bounds, effectively invisible ("hidden
  // behind the board"). Portaling to document.body and positioning with viewport coordinates
  // (position: fixed) escapes every ancestor's overflow clipping and stacking context.
  const [coords, setCoords] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Compute the dropdown's viewport position from the trigger whenever it opens. A layout effect
  // (not a plain effect) so this runs before the browser paints -- otherwise the portalled
  // dropdown would render at its default (0,0)-ish position for one visible frame before jumping
  // to the correct spot.
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  // Close on outside click -- must check both the trigger (containerRef) and the portalled
  // dropdown (dropdownRef), since the dropdown is no longer a DOM descendant of containerRef.
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e) {
      if (containerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery('');
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  // Close on scroll (of the window or any scrollable ancestor, e.g. the draft board's own
  // scroll container) rather than continuously repositioning -- simpler and avoids the dropdown
  // visually detaching from a trigger that's mid-scroll. The `true` (capture) option is required
  // to observe scroll on a nested scrollable ancestor, since scroll events don't bubble.
  useEffect(() => {
    if (!open) return;
    function handleScroll() {
      setOpen(false);
      setQuery('');
    }
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Pre-populate search input with suggestedName when opened with no linked opponent
  useEffect(() => {
    if (open && suggestedName && !currentOpponent) {
      setQuery(suggestedName);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = query.trim();
  const filtered = opponents
    .filter(o => o.displayName.toLowerCase().includes(trimmed.toLowerCase()))
    .slice(0, 8);

  const hasExactMatch = opponents.some(
    o => o.displayName.toLowerCase() === trimmed.toLowerCase()
  );

  function handleTrigger() {
    if (loading) return;
    setOpen(o => !o);
    setQuery('');
  }

  function handleSelect(opponentId) {
    setOpen(false);
    setQuery('');
    onLink?.(opponentId);
  }

  function handleUnlink() {
    setOpen(false);
    setQuery('');
    onUnlink?.();
  }

  function handleCreateAndLink() {
    if (!trimmed) return;
    setOpen(false);
    setQuery('');
    onCreateAndLink?.(trimmed);
  }

  function handleSuggestedCreate() {
    setDialogName(suggestedName);
    setConfirmDialog(true);
  }

  function handleDialogSave() {
    if (!dialogName.trim()) return;
    setConfirmDialog(false);
    setOpen(false);
    setQuery('');
    onCreateAndLink?.(dialogName.trim());
  }

  function handleDialogCancel() {
    setConfirmDialog(false);
  }

  const triggerLabel = loading
    ? '…'
    : currentOpponent
      ? currentOpponent.displayName
      : 'Link manager';

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger */}
      <button
        type='button'
        onClick={handleTrigger}
        disabled={loading}
        style={{
          background: 'none',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '2px 8px',
          fontSize: '0.8rem',
          cursor: loading ? 'default' : 'pointer',
          color: currentOpponent ? 'var(--text)' : 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
        }}
      >
        {triggerLabel}
        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>▾</span>
      </button>

      {/* Dropdown -- portalled to document.body (see coords effect above) so it always renders
          above/outside any ancestor's overflow clipping or stacking context, regardless of where
          this component is placed (a scrolling draft board table cell, a plain card, etc). */}
      {open && coords && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: 200,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            minWidth: 220,
            maxWidth: 300,
          }}
        >
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              ref={inputRef}
              type='text'
              placeholder='Search managers…'
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: '0.85rem',
                outline: 'none',
              }}
            />
          </div>

          {/* Options */}
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {/* Remove link row */}
            {currentOpponent && (
              <button
                type='button'
                onClick={handleUnlink}
                style={rowStyle(true)}
              >
                <span style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>✕</span>
                <span style={{ color: 'var(--danger)' }}>Remove link</span>
              </button>
            )}

            {/* Existing opponents */}
            {filtered.map(o => (
              <button
                key={o.id}
                type='button'
                onClick={() => handleSelect(o.id)}
                style={rowStyle(o.id === currentOpponent?.id)}
              >
                <span style={{ fontWeight: o.id === currentOpponent?.id ? 600 : 400 }}>
                  {o.displayName}
                </span>
                {o.nickname && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>
                    {o.nickname}
                  </span>
                )}
              </button>
            ))}

            {/* Create & link row */}
            {trimmed && !hasExactMatch && (
              <button
                type='button'
                onClick={handleCreateAndLink}
                style={rowStyle(false)}
              >
                <span style={{ color: 'var(--primary)', fontSize: '0.8rem' }}>＋</span>
                <span style={{ color: 'var(--primary)' }}>
                  Create &amp; link &ldquo;{trimmed}&rdquo;
                </span>
              </button>
            )}

            {/* Empty state */}
            {filtered.length === 0 && !trimmed && (
              <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                No managers yet. Type a name to create one.
              </div>
            )}
          </div>

          {/* Quick-create for suggested name */}
          {suggestedName && !currentOpponent && !confirmDialog && (
            <div style={{ borderTop: '1px solid var(--border)', padding: '6px 8px' }}>
              <button
                type='button'
                onClick={handleSuggestedCreate}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: '6px 4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ color: 'var(--primary)', fontSize: '0.8rem' }}>＋</span>
                <span style={{ color: 'var(--primary)' }}>Create as &ldquo;{suggestedName}&rdquo;</span>
              </button>
            </div>
          )}

          {/* Inline confirm/edit dialog */}
          {confirmDialog && (
            <div style={{ padding: '12px', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                Create manager
              </div>
              <input
                type='text'
                value={dialogName}
                onChange={e => setDialogName(e.target.value)}
                style={{
                  width: '100%',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  fontSize: '0.85rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button
                  type='button'
                  onClick={handleDialogSave}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    background: 'var(--primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  Save
                </button>
                <button
                  type='button'
                  onClick={handleDialogCancel}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function rowStyle(active) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    background: active ? 'var(--bg)' : 'none',
    color: 'var(--text)',
    border: 'none',
    padding: '8px 12px',
    fontSize: '0.875rem',
    cursor: 'pointer',
    textAlign: 'left',
  };
}

export default OpponentPicker;
